/* @flow */

import React from 'react';
import {connect} from 'react-redux';
import moment from 'moment';

import * as reactGA from 'lib/analytics';

import LoadingComponent from 'components/loading';

import SubscriptionTab2ViewComponent from './view';

import PersonalInfoForm from './model';

import RegisterService from 'services/registerService';
import {handleApiFormResponse, handleFormChange, handleFormValidation} from 'lib/forms';
import loadConstraints from 'lib/validation/loadConstraints';

import NotificationHelper from 'lib/notifications';

type SubscriptionTab2ComponentProps = {
  activeCompanyId: number;
  successHandler: () => void;
}

type SubscriptionTab2ComponentState = {
  constraints: any;
  errors: Map<string, ?string>;
  form: PersonalInfoForm;
  formErrors: string[];
  isLoading: boolean;
  isProcessing: boolean;
}

class SubscriptionTab2Component extends React.Component<SubscriptionTab2ComponentProps, SubscriptionTab2ComponentState> {

  constructor(props) {
    super(props);

    this.state = {
      constraints: null,
      errors: null,
      form: null,
      formErrors: [],
      isLoading: true,
      isProcessing: false
    };
  }

  componentDidMount = async () => {
    reactGA.initializeGA();
    reactGA.logEvent('Subscription', 'Start Personal Details', `Company ID: ${this.props.activeCompanyId}`);

    try {
      // Load person info
      const response = await RegisterService.getPersonInfo(this.props.activeCompanyId);

      // Load constraints
      const constraints = await loadConstraints('Person', ['titleId', 'lastName', 'firstName', 'email', 'phone', 'mobile',
        'addressLine1', 'addressLine2', 'zipCode', 'city', 'countryId', 'birthCountryId', 'birthDate', 'birthPlace',
        'nationalityId', 'registrationTypeId', 'registrationNumber', 'registrationExpireDate']);

      this.setState({
        constraints,
        errors: new Map(Object.keys(constraints).map(e => [e, undefined])),
        form: new PersonalInfoForm(response),
        isLoading: false
      });
    } catch (e) {
      console.error(e);
      NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
    }
  };

  componentWillUnmount = async () => {
    reactGA.logEvent('Subscription', 'Personal Details Validated', `Company ID: ${this.props.activeCompanyId}`);
  }

  handleChange = (fieldName: string) => (event) => handleFormChange.call(this, fieldName, event.target.value);
  handleDateChange = (fieldName: string) => (date) => handleFormChange.call(this, fieldName, date);
  handleTypeChange = (fieldName: string) => (id: number) => handleFormChange.call(this, fieldName, id);
  handlePhoneChange = (value: string) => {
    handleFormChange.call(this, 'phone', value);
  }
  handleMobileChange = (value: string) => {
    handleFormChange.call(this, 'mobile', value);
  }
  validateForm = () => handleFormValidation.call(this);

  submitForm = async () => {
    if (!this.validateForm()) return;
    this.setState({formErrors: [], isProcessing: true});

    try {
      const request = this.state.form.toRequest(this.props.activeCompanyId);
      await RegisterService.setPersonInfo(request);
      this.props.successHandler();
    } catch (e) {
      handleApiFormResponse.call(this, e);
    }
  };

  render = () => {
    if (this.state.isLoading) return <LoadingComponent />;

    return (
      <SubscriptionTab2ViewComponent
        errors={this.state.errors}
        form={this.state.form}
        formErrors={this.state.formErrors}
        isProcessing={this.state.isProcessing}
        maxDateBirth={moment().subtract(18, 'y').startOf('d')}
        minDateBirth={moment('01-01-1900', 'MM-DD-YYYY')}
        maxRegistrationExpireDate={moment().add(100, 'y').startOf('d')}
        minRegistrationExpireDate={moment().add(1, 'd').startOf('d')}
        handleChange={this.handleChange}
        handleDateChange={this.handleDateChange}
        handleTypeChange={this.handleTypeChange}
        handlePhoneChange={this.handlePhoneChange}
        handleMobileChange={this.handleMobileChange}
        submitForm={this.submitForm}
      />
    );
  }
}

const mapStateToProps = state => ({
  activeCompanyId: state.auth.user.activeCompany.id
});

export default connect(mapStateToProps)(SubscriptionTab2Component);
